/**
 * ClientCells (N4a) — ONE source of truth for how a lead's client details render,
 * shared by the All Clients / New Leads list and the Follow-Ups rows. Each field
 * is a fixed-width inline cell so a flex row still scans like aligned columns.
 *
 * Callers pass the ordered `fields` they want; the per-field renderers live here
 * only (no parallel copy). The follow-up-specific bits (the follow-up's own
 * date/time, its assignee chip, Mark-complete) stay in FollowUpsPage — this
 * component is purely the client-detail columns.
 */

import React from 'react';
import { CRM } from '../lib/theme';
import { phoneOrDash } from '../lib/format';
import { stageColor, stageBg } from '../lib/stages';
import { formatDate } from '../lib/utils';
import { todayInTz } from '../lib/time';
import { formatJobNo } from '../lib/jobNumber';

const C = { ...CRM, mono: "'DM Mono', monospace" };

export type ClientField = 'job' | 'client' | 'clientName' | 'phone' | 'stage' | 'type' | 'date' | 'rep' | 'source' | 'destination' | 'nextFollowup';

// Column width + header label per field. Widths keep flex rows aligned.
const FIELD: Record<ClientField, { label: string; width: number; grow?: boolean }> = {
  job: { label: 'Job #', width: 74 },
  client: { label: 'Client', width: 180, grow: true },
  clientName: { label: 'Client', width: 160, grow: true }, // name only (no email) — Follow-Ups variant
  phone: { label: 'Phone', width: 120 },
  stage: { label: 'Stage', width: 96 },
  type: { label: 'Type', width: 130 },
  date: { label: 'Move date', width: 96 },
  rep: { label: 'Rep', width: 110 },
  source: { label: 'Source', width: 100 },
  destination: { label: 'Destination', width: 160, grow: true },
  nextFollowup: { label: 'Next follow-up', width: 150 },
};

const cellStyle = (f: ClientField): React.CSSProperties => ({
  width: FIELD[f].width, flex: FIELD[f].grow ? `1 1 ${FIELD[f].width}px` : `0 0 ${FIELD[f].width}px`,
  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px',
});

export function repName(users: any[], id: string | null | undefined): string | null {
  if (!id) return null;
  const u = users.find((x) => x.id === id);
  return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '—';
}

// The next-scheduled-follow-up cell (Item 5): date, overdue-red, assignee chip
// when it isn't the viewer; a distinct empty state when there is none.
function NextFollowupCell({ lead, users, viewerId, tz }: { lead: any; users: any[]; viewerId?: string; tz: string }) {
  const nf = lead.next_follow_up;
  if (!nf) return <span style={{ color: 'var(--crm-dim)', fontStyle: 'italic' }}>None scheduled</span>;
  const overdue = nf.follow_up_date < todayInTz(tz);
  const assignee = nf.assigned_to_user_id && nf.assigned_to_user_id !== viewerId ? repName(users, nf.assigned_to_user_id) : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ fontFamily: C.mono, color: overdue ? 'var(--crm-red)' : 'var(--crm-amber)' }}>{formatDate(nf.follow_up_date)}{overdue ? ' · overdue' : ''}</span>
      {assignee && <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--crm-accent)', background: 'var(--crm-accent-tint)', padding: '1px 5px', borderRadius: '4px' }}>{assignee}</span>}
    </span>
  );
}

export function ClientCells({ lead, users, fields, viewerId, tz }: { lead: any; users: any[]; fields: ClientField[]; viewerId?: string; tz: string }) {
  return (
    <>
      {fields.map((f) => {
        let content: React.ReactNode;
        switch (f) {
          case 'job': content = <span style={{ color: C.dim, fontFamily: C.mono }}>{formatJobNo(lead)}</span>; break;
          case 'client': content = (
            <span style={{ color: C.text, fontWeight: 500 }}>
              {lead.first_name} {lead.last_name}
              {lead.email && <span style={{ color: C.dim, fontWeight: 400 }}> · {lead.email}</span>}
            </span>
          ); break;
          case 'clientName': content = <span style={{ color: C.text, fontWeight: 500 }}>{lead.first_name} {lead.last_name}</span>; break;
          case 'phone': content = <span style={{ color: C.muted, fontFamily: C.mono }}>{phoneOrDash(lead.phone)}</span>; break;
          case 'stage': content = <span style={{ background: stageBg(lead.stage), color: stageColor(lead.stage), padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>{lead.stage}</span>; break;
          case 'type': content = <span style={{ color: C.muted }}>{lead.move_type || '—'}</span>; break;
          case 'date': content = <span style={{ color: C.muted, fontFamily: C.mono }}>{lead.move_date || '—'}</span>; break;
          case 'rep': content = <span style={{ color: lead.assigned_rep_id ? C.muted : C.dim }}>{repName(users, lead.assigned_rep_id) || 'Unassigned'}</span>; break;
          case 'source': content = <span style={{ color: C.muted }}>{lead.lead_source || '—'}</span>; break;
          case 'destination': content = <span style={{ color: C.muted }}>{lead.destination_address || '—'}</span>; break;
          case 'nextFollowup': content = <NextFollowupCell lead={lead} users={users} viewerId={viewerId} tz={tz} />; break;
        }
        return <div key={f} style={cellStyle(f)}>{content}</div>;
      })}
    </>
  );
}

export function ClientHeader({ fields }: { fields: ClientField[] }) {
  return (
    <>
      {fields.map((f) => (
        <div key={f} style={{ ...cellStyle(f), fontSize: '10px', fontWeight: 600, color: C.dim, textTransform: 'uppercase' }}>{FIELD[f].label}</div>
      ))}
    </>
  );
}

/**
 * PHONE (≤640px): one client as a stacked card instead of a row of fixed-width
 * columns, so nothing scrolls sideways. Name and job number, then phone and
 * stage, then the move date with the next follow-up (All Clients) or the move
 * type and destination (New Leads). The phone is PLAIN TEXT: calls start only
 * from the lead file. `children` is the row's own controls (Grab / Assign),
 * which must stop the click from opening the lead.
 */
export function ClientCard({ lead, users, viewerId, tz, variant, onOpen, children, selected, onToggleSelect }: {
  lead: any; users: any[]; viewerId?: string; tz: string;
  variant: 'all' | 'new';
  onOpen?: () => void;
  children?: React.ReactNode;
  /** Bulk selection (All Clients / New Leads): a checkbox before the name. */
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const name = `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || 'Unnamed';
  const line: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, fontSize: '13px' };
  const clip: React.CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <div
      onClick={onOpen}
      style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '12px 14px', borderBottom: '1px solid var(--crm-line)', cursor: onOpen ? 'pointer' : 'default' }}
    >
      <div style={line}>
        {onToggleSelect && (
          <input
            type="checkbox" checked={!!selected} aria-label={`Select ${name}`}
            onClick={(e) => e.stopPropagation()} onChange={onToggleSelect}
            style={{ width: 20, height: 20, flexShrink: 0, margin: 0 }}
          />
        )}
        <span style={{ ...clip, flex: 1, color: C.text, fontWeight: 600, fontSize: '15px' }}>{name}</span>
        <span style={{ color: C.dim, fontFamily: C.mono, fontSize: '12px', flexShrink: 0 }}>{formatJobNo(lead)}</span>
      </div>
      <div style={line}>
        <span style={{ color: C.muted, fontFamily: C.mono, flexShrink: 0 }}>{phoneOrDash(lead.phone)}</span>
        {variant === 'all' && (
          <span style={{ background: stageBg(lead.stage), color: stageColor(lead.stage), padding: '2px 8px', borderRadius: '4px', fontSize: '11px', flexShrink: 0 }}>{lead.stage}</span>
        )}
        {variant === 'new' && <span style={{ ...clip, color: C.muted }}>{lead.move_type || '—'}</span>}
      </div>
      <div style={{ ...line, fontSize: '12px' }}>
        <span style={{ color: C.muted, fontFamily: C.mono, flexShrink: 0 }}>{lead.move_date || 'No move date'}</span>
        {variant === 'all'
          ? <span style={{ ...clip, flex: 1, textAlign: 'right' }}><NextFollowupCell lead={lead} users={users} viewerId={viewerId} tz={tz} /></span>
          : <span style={{ ...clip, flex: 1, textAlign: 'right', color: C.muted }}>{lead.destination_address || '—'}</span>}
      </div>
      {children}
    </div>
  );
}
